import { type IntlShape } from "react-intl";

import { DestinationType, type LensDestination, type Maybe, type QrCode } from "../../gql-generated/types";
import { type LensFolderRoleTypePermissions, ROLE_ACTIONS } from "../../shared/constants";
import { isLensApplierSourceApplication } from "../components/lens-source/utils/isLensApplierSourceApplication";
import { type LensProps } from "../hooks/LensProps";
import { makeDeleteLensPermissions } from "../hooks/lens-delete";
import { getLensScanCode } from "../utils/getLensScanCode";

import { type LensActionsState } from "./LensActionsState";

export const setScanCodePermissions =
    (qrCode: Maybe<QrCode> | undefined) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;

        const deeplinkExist = Boolean(qrCode?.deeplink);

        const { downloadQrCode, copyDeeplink } = actionsConfig;

        return {
            ...lensActionsState,
            actionsConfig: {
                ...actionsConfig,
                downloadQrCode: downloadQrCode && deeplinkExist && Boolean(getLensScanCode(qrCode)),
                copyDeeplink: copyDeeplink && deeplinkExist,
            },
        };
    };

export const setDeleteLensPermissions =
    (
        lensProps: LensProps,
        destinations: readonly LensDestination[],
        intl: IntlShape,
        lensFolderPermissions: LensFolderRoleTypePermissions,
    ) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const hasPermissions = lensFolderPermissions.ACTIONS.includes(ROLE_ACTIONS.LENS_FOLDER_LENS_DELETE);
        const deleteLensPermission = makeDeleteLensPermissions(lensProps, intl, hasPermissions, destinations);

        return {
            lensActionsErrorMap: {
                ...lensActionsState.lensActionsErrorMap,
                deleteLens: deleteLensPermission.reason,
            },
            actionsConfig: {
                ...lensActionsState.actionsConfig,
                deleteLens: deleteLensPermission.isPermitted,
            },
        };
    };

export const setPublishToSnapchatPermissions =
    (
        { rejectionErrors, distributionsList, sourceApplication }: LensProps,
        lensFolderPermissions: LensFolderRoleTypePermissions,
        lensMakerDebugEnabled: boolean,
    ) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;

        const hasRejectionErrors =
            !!rejectionErrors &&
            !!(
                rejectionErrors.heading ||
                rejectionErrors.messagesList.length > 0 ||
                rejectionErrors.violatingComponentsList.length > 0
            );

        if (distributionsList.some((d) => d.destinationType === DestinationType.DestinationTypeCommunity)) {
            return {
                ...lensActionsState,

                actionsConfig: {
                    ...actionsConfig,
                    publishToSnapchatConfig: {
                        disabled: true,
                        show: false,
                    },
                },
            };
        }

        // Show and disable if lens is from Lens Applier (and not allowlisted)
        if (isLensApplierSourceApplication(sourceApplication) && !lensMakerDebugEnabled) {
            return {
                ...lensActionsState,

                actionsConfig: {
                    ...actionsConfig,
                    publishToSnapchatConfig: {
                        disabled: true,
                        show: true,
                    },
                },
            };
        }

        const hasPermissions = lensFolderPermissions.ACTIONS.includes(ROLE_ACTIONS.PUBLISH_LENS);

        if (hasRejectionErrors || !hasPermissions) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    publishToSnapchatConfig: {
                        ...actionsConfig.publishToSnapchatConfig,
                        disabled: true,
                    },
                },
            };
        }

        return lensActionsState;
    };

export const setSetVisibilityPermissions =
    (lensFolderPermissions: LensFolderRoleTypePermissions, lensProps: LensProps, lensMakerDebugEnabled: boolean) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;
        if (!actionsConfig.setVisibility) {
            return lensActionsState;
        }

        const hasPermissionsBasedOnSourceApplication =
            lensMakerDebugEnabled || !isLensApplierSourceApplication(lensProps.sourceApplication);
        const hasPermissions =
            lensFolderPermissions.ACTIONS.includes(ROLE_ACTIONS.UPDATE_LENS_VISIBILITY) &&
            hasPermissionsBasedOnSourceApplication;

        if (!hasPermissions) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    setVisibility: false,
                },
            };
        }

        return lensActionsState;
    };

export const setEnrollCreatorPayoutPermissions =
    (lensFolderPermissions: LensFolderRoleTypePermissions) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;

        const hasPermissions = lensFolderPermissions.ACTIONS.includes(ROLE_ACTIONS.VIEW_CREATOR_REWARDS);

        if (!hasPermissions) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    enrollCreatorPayout: {
                        disabled: true,
                        show: false,
                    },
                },
            };
        }

        return lensActionsState;
    };

export const setManageDigitalGoodsPermissions =
    (lensProps: LensProps) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;
        if (isLensApplierSourceApplication(lensProps.sourceApplication)) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    manageDigitalGoods: false,
                },
            };
        }

        return lensActionsState;
    };

export const setFinalizePermissions =
    (lensProps: LensProps) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;
        if (isLensApplierSourceApplication(lensProps.sourceApplication)) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    finalize: false,
                },
            };
        }

        return lensActionsState;
    };

export const setLensCtaPermissions =
    (lensProps: LensProps, lensMakerDebugEnabled: boolean) =>
    (lensActionsState: LensActionsState): LensActionsState => {
        const { actionsConfig } = lensActionsState;
        if (!lensMakerDebugEnabled && isLensApplierSourceApplication(lensProps.sourceApplication)) {
            return {
                ...lensActionsState,
                actionsConfig: {
                    ...actionsConfig,
                    setLensCta: false,
                },
            };
        }

        return lensActionsState;
    };
